import { useState, useEffect } from 'react';

export type Language = {
  code: string;
  name: string;
  flag?: string;
};

export const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' }
];

export const translations = {
  en: {
    hero: {
      title: 'Advanced Trading Bots',
      subtitle: 'Powered by AI',
      description: 'Automate your trading strategy with our advanced AI-powered bots. Get started today and experience the future of algorithmic trading.',
      cta: 'Get Started'
    },
    features: {
      ai: {
        title: 'AI-Powered Trading',
        description: 'Advanced algorithms analyze market trends and execute trades with precision and speed.'
      },
      risk: {
        title: 'Risk Management',
        description: 'Built-in risk management features protect your investments and optimize returns.'
      },
      execution: {
        title: 'Real-time Execution',
        description: 'Lightning-fast execution ensures you never miss a trading opportunity.'
      }
    },
    auth: {
      email: 'Email',
      password: 'Password',
      login: 'Log in',
      register: 'Register',
      resetPassword: 'Reset Password',
      createAccount: 'Create Account',
      forgotPassword: 'Forgot Password?',
      passwordRequirement: 'Password must be at least 6 characters long',
      backToLogin: 'Back to Login',
      resetInstructions: 'Enter your email address and we\'ll send you instructions to reset your password.',
      processing: 'Processing...',
      acceptTerms: 'I accept the'
    },
    nav: {
      dashboard: 'Dashboard',
      tradingBots: 'Trading Bots',
      admin: 'Admin',
      settings: 'Settings',
      signOut: 'Sign Out'
    },
    dashboard: {
      welcome: 'Welcome back',
      liveTrades: 'Live Trades',
      myAssets: 'My Assets',
      noBots: {
        title: 'No Bots Selected',
        description: 'You haven\'t selected any trading bots yet.',
        action: 'Browse Available Bots'
      }
    },
    footer: {
      organization: 'Organization',
      legal: 'Legal',
      social: 'Social Media',
      terms: 'Terms of Use',
      privacy: 'Privacy Policy',
      faq: 'FAQ',
      about: 'About Us',
      telegram: 'Telegram',
      rights: 'All rights reserved.'
    },
    disclaimer: 'This application is only for club members\' sharing, learning and entertainment purposes',
    tradingBots: {
      topPerforming: 'Top Performing Bots',
      topPerformingDesc: 'Discover our most successful trading strategies',
      startTrading: 'Start Trading',
      riskLevels: {
        title: 'Risk Level',
        low: 'Low Risk',
        medium: 'Medium Risk',
        high: 'High Risk'
      },
      metrics: {
        pnl: 'PnL',
        running: 'Running',
        days: 'd'
      },
      status: {
        title: 'Status',
        active: 'Active',
        inactive: 'Inactive'
      }
    }
  },
  zh: {
    hero: {
      title: '高级交易机器人',
      subtitle: 'AI驱动',
      description: '使用我们的AI驱动的高级机器人自动化您的交易策略。立即开始，体验算法交易的未来。',
      cta: '立即开始'
    },
    features: {
      ai: {
        title: 'AI驱动交易',
        description: '先进的算法分析市场趋势，精确快速地执行交易。'
      },
      risk: {
        title: '风险管理',
        description: '内置风险管理功能保护您的投资并优化回报。'
      },
      execution: {
        title: '实时执行',
        description: '闪电般的执行速度确保您永远不会错过交易机会。'
      }
    },
    auth: {
      email: '邮箱',
      password: '密码',
      login: '登录',
      register: '注册',
      resetPassword: '重置密码',
      createAccount: '创建账户',
      forgotPassword: '忘记密码？',
      passwordRequirement: '密码必须至少6个字符',
      backToLogin: '返回登录',
      resetInstructions: '输入您的邮箱地址，我们将向您发送重置密码的说明。',
      processing: '处理中...',
      acceptTerms: '我接受'
    },
    nav: {
      dashboard: '仪表板',
      tradingBots: '交易机器人',
      admin: '管理员',
      settings: '设置',
      signOut: '退出登录'
    },
    dashboard: {
      welcome: '欢迎回来',
      liveTrades: '实时交易',
      myAssets: '我的资产',
      noBots: {
        title: '未选择机器人',
        description: '您还没有选择任何交易机器人。',
        action: '浏览可用机器人'
      }
    },
    footer: {
      organization: '组织',
      legal: '法律',
      social: '社交媒体',
      terms: '使用条款',
      privacy: '隐私政策',
      faq: '常见问题',
      about: '关于我们',
      telegram: '电报群',
      rights: '版权所有'
    },
    disclaimer: '本应用程序仅供俱乐部会员分享、学习和娱乐使用',
    tradingBots: {
      topPerforming: '表现最佳的机器人',
      topPerformingDesc: '发现我们最成功的交易策略',
      startTrading: '开始交易',
      riskLevels: {
        title: '风险等级',
        low: '低风险',
        medium: '中等风险',
        high: '高风险'
      },
      metrics: {
        pnl: '盈亏',
        running: '运行时间',
        days: '天'
      },
      status: {
        title: '状态',
        active: '运行中',
        inactive: '已停止'
      }
    }
  },
  ja: {
    hero: {
      title: '高度な取引ボット',
      subtitle: 'AI搭載',
      description: '当社の高度なAI搭載ボットで取引戦略を自動化。今すぐ始めて、アルゴリズム取引の未来を体験してください。',
      cta: '始める'
    },
    features: {
      ai: {
        title: 'AI搭載取引',
        description: '高度なアルゴリズムが市場トレンドを分析し、正確かつ迅速に取引を実行します。'
      },
      risk: {
        title: 'リスク管理',
        description: '内蔵のリスク管理機能が投資を保護し、リターンを最適化します。'
      },
      execution: {
        title: 'リアルタイム実行',
        description: '超高速の実行により、取引機会を逃しません。'
      }
    },
    auth: {
      email: 'メールアドレス',
      password: 'パスワード',
      login: 'ログイン',
      register: '登録',
      resetPassword: 'パスワードリセット',
      createAccount: 'アカウント作成',
      forgotPassword: 'パスワードをお忘れですか？',
      passwordRequirement: 'パスワードは6文字以上である必要があります',
      backToLogin: 'ログインに戻る',
      resetInstructions: 'メールアドレスを入力してください。パスワードリセットの手順を送信します。',
      processing: '処理中...',
      acceptTerms: '私は以下に同意します'
    },
    nav: {
      dashboard: 'ダッシュボード',
      tradingBots: '取引ボット',
      admin: '管理者',
      settings: '設定',
      signOut: 'ログアウト'
    },
    dashboard: {
      welcome: 'お帰りなさい',
      liveTrades: 'リアルタイム取引',
      myAssets: '資産状況',
      noBots: {
        title: 'ボット未選択',
        description: 'まだ取引ボットを選択していません。',
        action: '利用可能なボットを見る'
      }
    },
    footer: {
      organization: '組織',
      legal: '法的情報',
      social: 'ソーシャルメディア',
      terms: '利用規約',
      privacy: 'プライバシーポリシー',
      faq: 'よくある質問',
      about: '会社概要',
      telegram: 'Telegram',
      rights: '無断複写・転載を禁じます'
    },
    disclaimer: 'このアプリケーションはクラブメンバーの共有、学習、エンターテイメント目的のみに使用されます',
    tradingBots: {
      topPerforming: 'トップパフォーマンスボット',
      topPerformingDesc: '最も成功している取引戦略を見つける',
      startTrading: '取引を開始',
      riskLevels: {
        title: 'リスクレベル',
        low: '低リスク',
        medium: '中リスク',
        high: '高リスク'
      },
      metrics: {
        pnl: '損益',
        running: '稼働期間',
        days: '日'
      },
      status: {
        title: '状態',
        active: '稼働中',
        inactive: '停止中'
      }
    }
  },
  ko: {
    hero: {
      title: '고급 트레이딩 봇',
      subtitle: 'AI 기반',
      description: '당사의 AI 기반 고급 봇으로 트레이딩 전략을 자동화하세요. 지금 시작하고 알고리즘 트레이딩의 미래를 경험하세요.',
      cta: '시작하기'
    },
    features: {
      ai: {
        title: 'AI 기반 트레이딩',
        description: '고급 알고리즘이 시장 트렌드를 분석하고 정확하고 신속하게 거래를 실행합니다.'
      },
      risk: {
        title: '리스크 관리',
        description: '내장된 리스크 관리 기능이 투자를 보호하고 수익을 최적화합니다.'
      },
      execution: {
        title: '실시간 실행',
        description: '초고속 실행으로 트레이딩 기회를 놓치지 않습니다.'
      }
    },
    auth: {
      email: '이메일',
      password: '비밀번호',
      login: '로그인',
      register: '회원가입',
      resetPassword: '비밀번호 재설정',
      createAccount: '계정 만들기',
      forgotPassword: '비밀번호를 잊으셨나요?',
      passwordRequirement: '비밀번호는 6자 이상이어야 합니다',
      backToLogin: '로그인으로 돌아가기',
      resetInstructions: '이메일 주소를 입력하시면 비밀번호 재설정 안내를 보내드립니다.',
      processing: '처리 중...',
      acceptTerms: '다음에 동의합니다'
    },
    nav: {
      dashboard: '대시보드',
      tradingBots: '트레이딩 봇',
      admin: '관리자',
      settings: '설정',
      signOut: '로그아웃'
    },
    dashboard: {
      welcome: '환영합니다',
      liveTrades: '실시간 거래',
      myAssets: '내 자산',
      noBots: {
        title: '선택된 봇 없음',
        description: '아직 트레이딩 봇을 선택하지 않았습니다.',
        action: '사용 가능한 봇 보기'
      }
    },
    footer: {
      organization: '조직',
      legal: '법적 고지',
      social: '소셜 미디어',
      terms: '이용약관',
      privacy: '개인정보처리방침',
      faq: '자주 묻는 질문',
      about: '회사 소개',
      telegram: '텔레그램',
      rights: '모든 권리 보유'
    },
    disclaimer: '이 애플리케이션은 클럽 회원의 공유, 학습 및 엔터테인먼트 목적으로만 사용됩니다',
    tradingBots: {
      topPerforming: '최고 성과 봇',
      topPerformingDesc: '가장 성공적인 트레이딩 전략을 발견하세요',
      startTrading: '트레이딩 시작',
      riskLevels: {
        title: '리스크 수준',
        low: '낮은 리스크',
        medium: '중간 리스크',
        high: '높은 리스크'
      },
      metrics: {
        pnl: '손익',
        running: '운영 기간',
        days: '일'
      },
      status: {
        title: '상태',
        active: '활성화',
        inactive: '비활성화'
      }
    }
  }
};

export const defaultLanguage = 'en';

const LANGUAGE_CHANGE_EVENT = 'languageChange';

export function useLanguage() {
  const [currentLang, setCurrentLangState] = useState(
    localStorage.getItem('language') || defaultLanguage
  );

  const setCurrentLang = (lang: string) => {
    localStorage.setItem('language', lang);
    setCurrentLangState(lang);
    window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT));
  };

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLangState(localStorage.getItem('language') || defaultLanguage);
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    };
  }, []);

  const t = (path: string) => {
    const keys = path.split('.');
    let value = translations[currentLang as keyof typeof translations] || translations[defaultLanguage];
    
    for (const key of keys) {
      value = (value as any)?.[key];
      if (!value) {
        value = translations[defaultLanguage];
        for (const defaultKey of keys) {
          value = (value as any)?.[defaultKey];
        }
        break;
      }
    }
    
    return value || path;
  };

  return { currentLang, setCurrentLang, t };
}
